/**
 * IndexedDB数据库管理 - 文言文背诵功能
 * 用于存储和检索背诵选项缓存和本地历史
 */

import type { RecitationOptions } from '../types';

class RecitationDB {
  private dbName = 'recitationDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private isInitialized = false;

  /**
   * 初始化数据库
   */
  async init(): Promise<IDBDatabase> {
    if (this.isInitialized && this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('数据库打开失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBRequest).result;
        this.isInitialized = true;
        console.log('背诵数据库连接成功');
        resolve(this.db!);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBRequest).result;
        console.log('创建/升级背诵数据库');

        // 创建选项缓存存储对象
        if (!db.objectStoreNames.contains('options')) {
          const store = db.createObjectStore('options', { keyPath: 'id' });
          store.createIndex('passageId', 'passage_id', { unique: false });
          store.createIndex('position', 'position', { unique: false });
          console.log('创建options存储对象');
        }

        // 创建本地背诵历史存储对象
        if (!db.objectStoreNames.contains('history')) {
          const store = db.createObjectStore('history', { keyPath: 'id' });
          store.createIndex('passageId', 'passage_id', { unique: false });
          store.createIndex('createdAt', 'created_at', { unique: false });
          console.log('创建history存储对象');
        }
      };
    });
  }

  /**
   * 保存背诵选项缓存
   */
  async saveOptions(options: RecitationOptions): Promise<string> {
    if (!this.isInitialized) {
      await this.init();
    }

    const id = `${options.passage_id}_${options.position}`;
    const optionsWithId = {
      id,
      ...options,
      created_at: new Date().getTime()
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['options'], 'readwrite');
      const store = transaction.objectStore('options');
      const request = store.put(optionsWithId);

      request.onsuccess = () => {
        console.log('选项缓存保存成功，ID:', id);
        resolve(id);
      };

      request.onerror = (event) => {
        console.error('选项缓存保存失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 获取背诵选项缓存
   */
  async getOptions(passageId: string, position: number): Promise<RecitationOptions | null> {
    if (!this.isInitialized) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['options'], 'readonly');
      const store = transaction.objectStore('options');
      const id = `${passageId}_${position}`;
      const request = store.get(id);

      request.onsuccess = (event) => {
        const result = (event.target as IDBRequest).result;
        if (result) {
          resolve(result);
        } else {
          resolve(null);
        }
      };

      request.onerror = (event) => {
        console.error('获取选项缓存失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 删除指定文言文的所有选项缓存
   */
  async deleteOptionsByPassageId(passageId: string): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['options'], 'readwrite');
      const store = transaction.objectStore('options');
      const index = store.index('passageId');
      const range = IDBKeyRange.only(passageId);
      
      // 首先获取所有匹配的键
      const keysRequest = index.getAllKeys(range);
      
      keysRequest.onsuccess = (event) => {
        const keys = (event.target as IDBRequest).result;
        console.log(`找到 ${keys.length} 个选项缓存需要删除`);
        
        if (keys.length === 0) {
          return resolve();
        }
        
        // 批量删除
        let deleteCount = 0;
        keys.forEach((key: string) => {
          const deleteRequest = store.delete(key);
          
          deleteRequest.onsuccess = () => {
            deleteCount++;
            if (deleteCount === keys.length) {
              console.log(`成功删除 ${deleteCount} 个选项缓存`);
              resolve();
            }
          };
          
          deleteRequest.onerror = (error) => {
            console.error(`删除选项缓存 ${key} 失败:`, error);
          };
        });
      };
      
      keysRequest.onerror = (event) => {
        console.error('获取选项缓存键失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 保存本地背诵历史
   */
  async saveHistory(history: {
    passage_id: string;
    level: number;
    accuracy: number;
    completed_at: number;
  }): Promise<string> {
    if (!this.isInitialized) {
      await this.init();
    }

    const id = `${history.passage_id}_${Date.now()}`;
    const historyWithId = {
      id,
      ...history,
      created_at: new Date().getTime()
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['history'], 'readwrite');
      const store = transaction.objectStore('history');
      const request = store.add(historyWithId);

      request.onsuccess = () => {
        console.log('背诵历史保存成功，ID:', id);
        resolve(id);
      };

      request.onerror = (event) => {
        console.error('背诵历史保存失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 获取指定文言文的背诵历史
   */
  async getHistoryByPassageId(passageId: string): Promise<any[]> {
    if (!this.isInitialized) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['history'], 'readonly');
      const store = transaction.objectStore('history');
      const index = store.index('passageId');
      const range = IDBKeyRange.only(passageId);
      const request = index.getAll(range);

      request.onsuccess = (event) => {
        const result = (event.target as IDBRequest).result;
        resolve(result || []);
      };

      request.onerror = (event) => {
        console.error('获取背诵历史失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 获取所有背诵历史
   */
  async getAllHistory(): Promise<any[]> {
    if (!this.isInitialized) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['history'], 'readonly');
      const store = transaction.objectStore('history');
      const index = store.index('createdAt');
      const request = index.openCursor(null, 'prev'); // 按创建时间降序排列
      
      const history: any[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          history.push(cursor.value);
          cursor.continue();
        } else {
          resolve(history);
        }
      };

      request.onerror = (event) => {
        console.error('获取背诵历史失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * 删除指定文言文的背诵历史
   */
  async deleteHistoryByPassageId(passageId: string): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['history'], 'readwrite');
      const store = transaction.objectStore('history');
      const index = store.index('passageId');
      const range = IDBKeyRange.only(passageId);
      
      const keysRequest = index.getAllKeys(range);
      
      keysRequest.onsuccess = (event) => {
        const keys = (event.target as IDBRequest).result;
        console.log(`找到 ${keys.length} 个背诵历史需要删除`);
        
        if (keys.length === 0) {
          return resolve();
        }
        
        let deleteCount = 0;
        keys.forEach((key: string) => {
          const deleteRequest = store.delete(key);
          
          deleteRequest.onsuccess = () => {
            deleteCount++;
            if (deleteCount === keys.length) {
              console.log(`成功删除 ${deleteCount} 个背诵历史`);
              resolve();
            }
          };
          
          deleteRequest.onerror = (error) => {
            console.error(`删除背诵历史 ${key} 失败:`, error);
          };
        });
      };
      
      keysRequest.onerror = (event) => {
        console.error('获取背诵历史键失败:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }
}

// 创建数据库实例
const recitationDB = new RecitationDB();

export default recitationDB;
